import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import AccountController from './account.controller';
import Page from '../common/page/Page';
import FormError from '../common/formError/FormError';
import PersonalInformationCard from './elements/PersonalInformationCard';
import PasswordCard from './elements/PasswordCard';
import AccountSuccessBanner from './elements/AccountSuccessBanner';
import { PAGE_METADATA } from './account.constants';
import Form from '../common/form/Form';

/**
 * Account Settings — view-only React component.
 *
 * Owns nothing but the orchestrator-level loading flag. All form state lives
 * in `account.controller.js`; this render is a thin composition of:
 *  
 *   - `Page`                       → page chrome (Layout + title + loading)
 *   - `AccountSuccessBanner`       → success message after a save
 *   - `FormError`                  → validation / API errors
 *   - `PersonalInformationCard`    → first / last name, email, phone
 *   - `PasswordCard`               → toggle + 3 password inputs
 *   - `Form`                       → Cancel / Save actions + confirm dialog
 *
 * Each element is dumb and pure — props in, callbacks out.
 */
class Account extends Inherit(AccountController, Component) {
    render() {
        const {
            loading,
            firstName,
            lastName,
            email,
            phoneNumber,
            currentPassword,
            newPassword,
            confirmPassword,
            showPasswordSection,
            saving,
            errorMessage,
            successMessage,
        } = this.state;

        const hasError = Boolean(errorMessage);

        return (
            <Page
                title={PAGE_METADATA.title}
                description={PAGE_METADATA.description}
                isLoading={loading}
                notificationContextHolder={this.props.notificationContextHolder}
            >
                <Form 
                    onSubmit={() => this.handleSaveChanges()}
                    onCancel={() => this.handleCancel()}
                    loading={loading || saving}
                    disabled={!this.hasAccountChanges()}
                    submitText="Save Changes"
                    confirmTitle="Save Changes"
                    confirmMessage="Are you sure you want to save changes?"
                    confirmOkText="Save"
                    confirmCancelText="Cancel"
                    confirmBeforeSubmit
                >
                    <AccountSuccessBanner message={successMessage} />
                    <PersonalInformationCard
                        firstName={firstName}
                        lastName={lastName}
                        email={email}
                        phoneNumber={phoneNumber}
                        hasError={hasError}
                        onFirstNameChange={(v) => this.setFirstName(v)}
                        onLastNameChange={(v) => this.setLastName(v)}
                        onEmailChange={(v) => this.setEmail(v)}
                        onPhoneNumberChange={(v) => this.setPhoneNumber(v)}
                    />
                    <PasswordCard
                        open={showPasswordSection}
                        currentPassword={currentPassword}
                        newPassword={newPassword}
                        confirmPassword={confirmPassword}
                        hasError={hasError}
                        onToggle={() => this.handleTogglePasswordSection()}
                        onCurrentPasswordChange={(v) => this.setCurrentPassword(v)}
                        onNewPasswordChange={(v) => this.setNewPassword(v)}
                        onConfirmPasswordChange={(v) => this.setConfirmPassword(v)}
                    />
                    <FormError error={errorMessage} />
                </Form>
            </Page>
        );
    }
}

export default withHooks(Account);
